import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportMapping } from '../../types';
import { ImportService } from '../../services/ImportService';
import { Save, Trash2, RotateCcw, X } from 'lucide-react';

interface MappingManagerProps {
    fileHeaders: string[];
    currentMapping: Record<string, string | string[]>;
    onMappingChange: (newMapping: Record<string, string | string[]>) => void;
    agencyId: string;
}

interface AssetField {
    key: string;
    labelKey: string;
}

const ASSET_FIELDS: AssetField[] = [
    { key: 'code', labelKey: 'import.mapping.code' },
    { key: 'description', labelKey: 'import.mapping.description' },
    { key: 'categoryId', labelKey: 'import.mapping.category' },
    { key: 'subcategory', labelKey: 'import.mapping.subcategory' },
    { key: 'quantity', labelKey: 'import.mapping.quantity' },
    { key: 'model', labelKey: 'import.mapping.model' },
    { key: 'manufacturer', labelKey: 'import.mapping.manufacturer' },
    { key: 'supplierDescription', labelKey: 'import.mapping.supplier_desc' },
    { key: 'supplierCode', labelKey: 'import.mapping.supplier_code' },
    { key: 'supplierVat', labelKey: 'import.mapping.supplier_vat' },
    { key: 'branchCode', labelKey: 'import.mapping.branch_code' },
    { key: 'serialNumber', labelKey: 'import.mapping.serial' },
    { key: 'masterSerial', labelKey: 'import.mapping.master_serial' },
    { key: 'barcode', labelKey: 'import.mapping.barcode' },

    // Financials
    { key: 'invoiceNumber', labelKey: 'import.mapping.invoice_number' },
    { key: 'invoiceDate', labelKey: 'import.mapping.invoice_date' },
    { key: 'acquisitionDate', labelKey: 'import.mapping.acquisition_date' },
    { key: 'sentToAccountingDate', labelKey: 'import.mapping.sent_accounting' },
    { key: 'usageStartDate', labelKey: 'import.mapping.usage_start_date' },
    { key: 'unitValue', labelKey: 'import.mapping.unit_value' },
    { key: 'discountStats', labelKey: 'import.mapping.discount' },
    { key: 'discount', labelKey: 'import.mapping.discount_stats' },
    { key: 'totalValue', labelKey: 'import.mapping.total_value' },
    { key: 'vatPercent', labelKey: 'import.mapping.vat_percent' },
    { key: 'residualValue', labelKey: 'import.mapping.residual_value' },

    // Relations
    { key: 'supplierId', labelKey: 'import.mapping.supplier' },
    { key: 'costCenterId', labelKey: 'import.mapping.cost_center' },
    { key: 'branchId', labelKey: 'import.mapping.branch' },
    { key: 'exactLocation', labelKey: 'import.mapping.exact_location' },

    // People
    { key: 'assetManagerId', labelKey: 'import.mapping.asset_manager' },
    { key: 'assetUserId', labelKey: 'import.mapping.asset_user' },

    // Physical Attributes
    { key: 'color', labelKey: 'import.mapping.color' },
    { key: 'height', labelKey: 'import.mapping.height' },
    { key: 'length', labelKey: 'import.mapping.length' },
    { key: 'width', labelKey: 'import.mapping.width' },

    { key: 'status', labelKey: 'import.mapping.status' },
    { key: 'notes', labelKey: 'import.mapping.notes' }
];

export const MappingManager: React.FC<MappingManagerProps> = ({
    fileHeaders, currentMapping, onMappingChange, agencyId
}) => {
    const { t } = useTranslation('assets');
    const [profiles, setProfiles] = useState<ImportMapping[]>([]);
    const [selectedProfileId, setSelectedProfileId] = useState<string>('');
    const [newProfileName, setNewProfileName] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadProfiles();
    }, [agencyId]);

    const loadProfiles = async () => {
        try {
            const mappings = await ImportService.getMappings(agencyId);
            setProfiles(mappings);
        } catch (e) {
            console.error("Failed to load mappings", e);
        }
    };

    const handleAddField = (header: string, fieldKey: string) => {
        if (!fieldKey) return;
        const newMap = { ...currentMapping };
        const current = newMap[header];

        if (Array.isArray(current)) {
            if (!current.includes(fieldKey)) {
                newMap[header] = [...current, fieldKey];
            }
        } else if (current) {
            if (current !== fieldKey) {
                newMap[header] = [current, fieldKey];
            }
        } else {
            newMap[header] = [fieldKey];
        }
        onMappingChange(newMap);
    };

    const handleRemoveField = (header: string, fieldKey: string) => {
        const newMap = { ...currentMapping };
        const current = newMap[header];

        if (Array.isArray(current)) {
            const next = current.filter(k => k !== fieldKey);
            if (next.length === 0) delete newMap[header];
            else newMap[header] = next;
        } else if (current === fieldKey) {
            delete newMap[header];
        }
        onMappingChange(newMap);
    };

    const handleLoadProfile = (profileId: string) => {
        setSelectedProfileId(profileId);
        const profile = profiles.find(p => p.id === profileId);
        if (profile) {
            onMappingChange(profile.mapping);
        }
    };

    const handleSaveProfile = async () => {
        if (!newProfileName.trim()) return;
        setIsSaving(true);
        try {
            await ImportService.saveMapping({
                agencyId,
                name: newProfileName,
                type: 'asset',
                mapping: currentMapping
            });
            setNewProfileName('');
            await loadProfiles();
        } catch (e) {
            alert('Failed to save profile');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAutoMap = () => {
        const newMap: Record<string, string[]> = {};
        fileHeaders.forEach(header => {
            const normHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
            // Find all matches instead of just one
            const matches = ASSET_FIELDS.filter(f => {
                const normField = f.key.toLowerCase();
                const translatedLabel = t(f.labelKey).toLowerCase().replace(/[^a-z0-9]/g, '');
                return normHeader.includes(normField) || normHeader.includes(translatedLabel) || translatedLabel.includes(normHeader);
            });

            if (matches.length > 0) {
                // If we found exact match 'code', prefer it over 'supplierCode' if ambiguous, 
                // but here we just take the first one or all? 
                // Auto-map typically maps 1-1 to start with to avoid mess. 
                // Let's just take the first best match for now.
                newMap[header] = [matches[0].key];
            }
        });
        onMappingChange(newMap);
    };

    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-end bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="w-full md:w-1/3 flex gap-2 items-end">
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Load Saved Mapping</label>
                        <select
                            className="w-full p-2 rounded-lg border border-slate-300 text-sm"
                            value={selectedProfileId}
                            onChange={(e) => handleLoadProfile(e.target.value)}
                        >
                            <option value="">-- Select Profile --</option>
                            {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={async () => {
                            if (!selectedProfileId) return;
                            if (window.confirm('Delete this mapping profile?')) {
                                try {
                                    await ImportService.deleteMapping(selectedProfileId);
                                    await loadProfiles();
                                    setSelectedProfileId('');
                                } catch (e) {
                                    alert('Failed to delete profile');
                                }
                            }
                        }}
                        disabled={!selectedProfileId}
                        className="mb-0.5 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 disabled:opacity-50"
                        title="Delete Profile"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>

                <div className="w-full md:w-1/3 flex gap-2">
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Save New Profile</label>
                        <input
                            type="text"
                            placeholder="Profile Name..."
                            className="w-full p-2 rounded-lg border border-slate-300 text-sm"
                            value={newProfileName}
                            onChange={e => setNewProfileName(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={handleSaveProfile}
                        disabled={!newProfileName || isSaving}
                        className="mb-0.5 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        title="Save Profile"
                    >
                        <Save size={18} />
                    </button>
                </div>

                <button
                    onClick={handleAutoMap}
                    className="p-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-white flex items-center gap-2 text-sm"
                >
                    <RotateCcw size={16} /> Auto-Map
                </button>
            </div>

            {/* Mapping Grid */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm text-left">
                    <thead className="bg-slate-100 text-slate-700 font-semibold">
                        <tr>
                            <th className="p-3 border-b">CSV Header</th>
                            <th className="p-3 border-b">Asset Field</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {fileHeaders.map(header => (
                            <tr key={header} className="hover:bg-slate-50">
                                <td className="p-3 font-medium text-slate-900">{header}</td>
                                <td className="p-3">
                                    <div className="flex flex-col gap-2">
                                        {/* Current Selections */}
                                        {(() => {
                                            const raw = currentMapping[header];
                                            const selectedKeys = Array.isArray(raw) ? raw : (raw ? [raw] : []);

                                            return (
                                                <div className="flex flex-wrap gap-2 mb-1">
                                                    {selectedKeys.map(key => {
                                                        const field = ASSET_FIELDS.find(f => f.key === key);
                                                        return (
                                                            <div key={key} className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs border border-blue-200">
                                                                <span>{field ? t(field.labelKey) : key}</span>
                                                                <button
                                                                    onClick={() => handleRemoveField(header, key)}
                                                                    className="hover:text-blue-900"
                                                                >
                                                                    <X size={12} />
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}

                                        {/* Add Field Selector */}
                                        <select
                                            className="w-full p-2 rounded-lg border border-slate-200 text-sm"
                                            value=""
                                            onChange={(e) => handleAddField(header, e.target.value)}
                                        >
                                            <option value="">+ Add Field Mapping</option>
                                            {ASSET_FIELDS.map(f => (
                                                <option key={f.key} value={f.key}>{t(f.labelKey)}</option>
                                            ))}
                                        </select>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
